/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import {
	ListItem,
	ListItemText,
	List,
	Card,
	Chip,
	Typography,
	Accordion,
	AccordionSummary,
	Grid,
	AccordionDetails,
} from "@material-ui/core";
import { getLexBotV2LanguageIntents, getAllLexBotV2LanguageSlotTypes } from "../services/LexBotV2Service";
import { v2, errorToObject } from "@prftesp/cle-logger";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { CopyButton } from "../../agentConsole/components/Common/CopyButton";
import { InteractiveMessageModal } from "./InteractiveMessageModal";
import { CopyIconButton } from "../../../components/Common";

/**
 *This component is used to load and render the intents and slot types details to a display card.
 *
 * @param {Boolean} botId The bot id from the bot details used to fetch intents and slot types.
 * @param {String} botVersion The bot version from the bot details used to fetch intents and slot types for particular version.
 * @param {String} localeId The local id from the bot details used to fetch intents and slot types for particular language.
 */
export const IntentsAndSlotTypeSection = ({
	botId,
	botName,
	botAliasName,
	botVersion,
	locale,
	setBotDetailsLoading,
}) => {
	const [intentAndSlotTypeLoading, setIntentAndSlotTypeLoading] = React.useState(false);
	const [detailsLoaded, setDetailsLoaded] = React.useState(false);
	const [intents, setIntents] = React.useState([]);
	const [expanded, setExpanded] = React.useState(false);
	const [slotTypes, setSlotTypes] = React.useState([]);
	const handleBotLoading = React.useCallback((loading) => {
		setIntentAndSlotTypeLoading(loading);
	}, []);
	const handleChange = (_event, newExpanded) => {
		setExpanded(newExpanded);
	};
	React.useEffect(() => {
		const getIntentsAndSlotTypes = async () => {
			try {
				let promises = [
					getLexBotV2LanguageIntents(botId, botVersion, locale.localeId),
					getAllLexBotV2LanguageSlotTypes(botId, botVersion, locale.localeId),
				];
				promises = await Promise.all(promises);
				const intents = promises[0];
				const slotType = promises[1];
				setIntents(intents);
				setSlotTypes(slotType);
				setDetailsLoaded(true);
				handleBotLoading(false);
				setBotDetailsLoading(false);
			} catch (error) {
				v2.error(`Error loading lex bot details`, {
					additionalData: {
						error: errorToObject(error),
					},
				});
			}
		};
		if (!intentAndSlotTypeLoading && !detailsLoaded && expanded) {
			setBotDetailsLoading(true);
			handleBotLoading(true);
			getIntentsAndSlotTypes();
		}
	}, [
		intentAndSlotTypeLoading,
		handleBotLoading,
		detailsLoaded,
		botId,
		botVersion,
		locale,
		setBotDetailsLoading,
		expanded,
	]);

	return (
		<div style={{ width: "100%", padding: "10px" }}>
			<Accordion style={{ marginBottom: "10px" }} onChange={handleChange} key={locale.localeId}>
				<AccordionSummary
					style={{
						textAlign: "center",
						background: "#eeeeee",
						borderRadius: "5px",
						height: "40px",
					}}
					expandIcon={<ExpandMoreIcon />}
					aria-controls={locale.localeId}
					id={locale.localeId}
				>
					<AccordionDetails className="p-0 w-100 justify-content-between">
						<Typography style={{ paddingTop: "5px", display: "flex", fontWeight: 600 }}>
							{locale.localeName}
							<CopyIconButton value={locale.localeId} />
						</Typography>
						<Typography style={{ paddingTop: "5px" }}>
							<label style={{ fontWeight: 600, marginRight: "5px", display: "inline" }}> Status:</label>
							{locale.botLocaleStatus === "Built" ? (
								<span style={{ color: "green", display: "inline-block" }}>Ready</span>
							) : (
								<span style={{ color: "red", display: "inline-block" }}>Not built</span>
							)}
						</Typography>
					</AccordionDetails>
				</AccordionSummary>
				{!intentAndSlotTypeLoading && detailsLoaded ? (
					<>
						<div style={{ width: "100%", marginRight: "25px", padding: "0px 15px 0px 15px" }}>
							<h6
								style={{
									marginTop: "15px",
									background: "#eeeeee",
									borderRadius: "5px",
									width: "100%",
									fontWeight: 600,
									fontSize: "18px",
									marginRight: "10px",
									padding: "5px",
								}}
							>
								Intents
							</h6>
							{intents &&
								intents.map((intent) => (
									<Card
										key={intent.intentDetail.intentId}
										style={{ marginBottom: "15px", padding: "10px" }}
										variant="outlined"
									>
										<div style={{ display: "flex" }}>
											<div style={{ margin: "10px", minWidth: "40%" }}>
												<label style={{ fontSize: "13px", color: "gray", fontWeight: 600 }}>Name</label>
												<div style={{ wordBreak: "break-word" }}>
													<span>{intent.intentDetail.intentName || ""}</span>
													<CopyIconButton value={intent?.intentDetail?.intentName} />
												</div>
											</div>
											<div style={{ margin: "10px", minWidth: "50%" }}>
												<label style={{ fontSize: "13px", color: "gray", fontWeight: 600 }}>Description</label>
												<div style={{ wordBreak: "break-word" }}>{intent.intentDetail.description || ""}</div>
											</div>
										</div>
										<div style={{ textAlign: "left", margin: "10px 0 20px 0", paddingLeft: "10px" }}>
											<h6>Slots</h6>

											{!intent.intentDetail.slots?.length ? (
												<div style={{ textAlign: "left", color: "gray", paddingLeft: "10px" }}>
													No slots for this intent.
												</div>
											) : (
												<div
													style={{
														width: "100%",
														border: "1px solid lightGray",
													}}
												>
													<List style={{ padding: "0px" }}>
														{intent.intentDetail.slots.map((slot) => {
															const interactiveMessageKey = `${botName}_${botAliasName}_${locale.localeId}_${intent.intentDetail.intentName}_${slot.slotName}`;
															return (
																<ListItem
																	key={slot.slotId}
																	style={{ borderBottom: "1px solid whitesmoke", wordBreak: "break-word" }}
																>
																	<Grid container justifyContent="space-between">
																		<Grid item xs={12} lg={8}>
																			<ListItemText
																				primary={slot.slotName}
																				secondary={
																					<>
																						<span style={{ marginRight: 6 }}>{interactiveMessageKey}</span>
																						<CopyButton
																							value={interactiveMessageKey}
																							title="Copy interactive message key"
																							style={{ color: "#707070" }}
																						/>
																					</>
																				}
																			/>
																		</Grid>
																		<Grid item xs={12} lg={4} style={{ marginTop: "auto", marginBottom: "auto" }}>
																			<InteractiveMessageModal
																				interactiveMessageKey={interactiveMessageKey}
																				slot={slot}
																				slotTypes={slotTypes}
																			/>
																		</Grid>
																	</Grid>
																</ListItem>
															);
														})}
													</List>
												</div>
											)}
										</div>
										<div style={{ textAlign: "left", marginTop: "10px", paddingLeft: "10px" }}>
											<h6>Sample Phrases</h6>
											{!intent.intentDetail.sampleUtterances?.length ? (
												<div style={{ textAlign: "left", color: "gray", paddingLeft: "10px" }}>No phrases.</div>
											) : (
												<div
													style={{
														width: "100%",
														border: "1px solid lightGray",
													}}
												>
													<List style={{ padding: "0px" }}>
														{intent.intentDetail.sampleUtterances.map((phrase, index) => (
															<ListItem
																key={phrase + index}
																style={{ borderBottom: "1px solid whitesmoke", wordBreak: "break-word" }}
															>
																<ListItemText>{phrase.utterance}</ListItemText>
															</ListItem>
														))}
													</List>
												</div>
											)}
										</div>
									</Card>
								))}

							{intents && intents.length === 0 && (
								<div style={{ textAlign: "left", color: "gray", paddingLeft: "10px" }}>No intents.</div>
							)}
						</div>
						<div style={{ width: "100%", padding: "15px" }}>
							<h6
								style={{
									textAlign: "left",
									paddingLeft: "10px",
									background: "#eeeeee",
									borderRadius: "5px",
									width: "100%",
									padding: "5px",
									fontWeight: 600,
									fontSize: "18px",
								}}
							>
								Slot Types
							</h6>
							{slotTypes &&
								slotTypes.map((slotType) => (
									<Card
										key={slotType.slotType.slotTypeId}
										style={{ marginBottom: "15px", padding: "10px" }}
										variant="outlined"
									>
										<div style={{ margin: "10px", display: "flex" }}>
											<div style={{ margin: "10px", minWidth: "40%" }}>
												<label style={{ fontSize: "13px", color: "gray", fontWeight: 600 }}>Name</label>
												<div style={{ wordBreak: "break-word" }}>
													<span>{slotType.slotType.slotTypeName || ""} </span>{" "}
													<CopyIconButton value={slotType?.slotType?.slotTypeName} />
												</div>
											</div>
											<div style={{ margin: "10px", minWidth: "50%" }}>
												<label style={{ fontSize: "13px", color: "gray", fontWeight: 600 }}>Description</label>
												<div style={{ wordBreak: "break-word" }}>{slotType.slotType.description || ""}</div>
											</div>
										</div>
										<div style={{ textAlign: "left", paddingLeft: "10px", marginTop: "10px" }}>
											<h6>Slot Values</h6>
										</div>
										{!slotType.slotType.slotTypeValues ? (
											<div style={{ textAlign: "left", color: "gray", paddingLeft: "10px" }}>No slot values.</div>
										) : (
											slotType.slotType.slotTypeValues && (
												<div
													style={{
														width: "100%",
														border: "1px solid lightGray",
													}}
												>
													<List style={{ padding: "0px" }}>
														{slotType.slotType.slotTypeValues.map((sample, index) => (
															<ListItem
																key={sample + index}
																style={{ borderBottom: "1px solid whitesmoke", wordBreak: "break-word" }}
															>
																<ListItemText>
																	{sample.sampleValue.value}
																	{sample.synonyms && (
																		<div>
																			<label
																				style={{
																					marginRight: "10px",
																					fontSize: "12px",
																					color: "gray",
																					fontWeight: 600,
																				}}
																			>
																				{"Recognized Values"}
																			</label>
																			{sample.synonyms &&
																				sample.synonyms.map((synonym, i) => (
																					<Chip
																						key={synonym + i}
																						style={{ marginRight: "5px" }}
																						label={synonym.value}
																						variant="outlined"
																					/>
																				))}
																		</div>
																	)}
																</ListItemText>
															</ListItem>
														))}
													</List>
												</div>
											)
										)}
									</Card>
								))}
							{slotTypes && slotTypes.length === 0 && (
								<div style={{ textAlign: "left", color: "gray", paddingLeft: "10px" }}>No slot types.</div>
							)}
						</div>
					</>
				) : (
					<div>
						<i
							style={{ left: "50%", position: "relative", margin: "50px 0px" }}
							className="fas fa-spinner loading-spinner spin-animation"
						/>
					</div>
				)}
			</Accordion>
		</div>
	);
};
